// src/lib/auth/effective-role.ts

/**
 * PERSONA only. 'admin' is NOT a persona — administrative authority lives in
 * `public.admin_roles` and is resolved by `useIsAdmin()`. Routing asks
 * authority FIRST (src/app/index.tsx), so an administrator never needs one.
 * See .planning/ADMIN-AUTHORITY-MODEL.md.
 */
export type AppRole = 'startup' | 'investor';

export interface EffectiveRoleInput {
  /** A usable session exists (real Supabase user, or mock/demo bypass). */
  hasAuth: boolean;
  /** Mock / demo-bypass mode — the local store is authoritative (no real session). */
  isMockMode: boolean;
  /** A real Supabase user is present (distinct from mock). */
  hasUser: boolean;
  /** Role from the freshly-fetched DB profile. May be undefined (not loaded yet) or 'pending'. */
  profileRole?: string | null;
  /** Role from the persisted profile store — hydrated synchronously at `_hasHydrated`. May be 'pending'. */
  persistedRole?: string | null;
}

/**
 * Single source of truth for "what role is this user" used by app routing.
 *
 * Precedence — designed so a cold relaunch never bounces a returning user to
 * /select-role while the async DB profile is still loading:
 *   1. No session            → null
 *   2. Mock/demo bypass       → persisted store role (no real session to read)
 *   3. DB profile role        → wins when loaded and not 'pending' (server truth)
 *   4. Persisted store role   → fallback for a real session before/if the DB role
 *                               resolves. It's hydrated synchronously and kept in
 *                               sync with the DB by select-role + syncUserToProfileStore.
 *   5. Otherwise              → null (genuinely new/pending users → select-role;
 *                               sign-out clears the store, so persistedRole is null here)
 *
 * THIS FUNCTION ANSWERS PERSONA ONLY — IT NEVER ANSWERS "IS THIS AN ADMIN".
 *   Administrative authority is `public.admin_roles` membership, resolved by
 *   `useIsAdmin()`, and routing asks it FIRST (src/app/index.tsx). So an
 *   administrator never needs a persona and this function never needs to know
 *   one exists.
 *
 *   It used to return 'admin' straight off `profiles.role`. That value confers
 *   no authority — `is_admin()` reads membership alone — so the router sent
 *   such a user to /(admin), whose guard refused them and redirected to /login,
 *   which redirects a live session back to '/', which routed to /(admin) again:
 *   an infinite redirect loop surfacing as the generic ErrorBoundary screen
 *   (staging, 2026-08-07). The intermediate fix passed the membership in as
 *   `isCanonicalAdmin` so the two stores could agree; that reconciled the split
 *   rather than removing it, and is gone.
 *
 *   'admin' is now a RETIRED persona value (migration 20260807120000 backfilled
 *   it away and the trigger rejects it from every writer, service-role
 *   included). Encountering it means the data predates the migration, so it is
 *   treated as NO persona → /select-role: terminal, honest, loop-free.
 */
export function resolveEffectiveRole(input: EffectiveRoleInput): AppRole | null {
  const { hasAuth, isMockMode, hasUser, profileRole, persistedRole } = input;

  /** Neither placeholder nor the retired admin value is a usable persona. */
  const asPersona = (value: string | null | undefined): AppRole | null =>
    value === 'startup' || value === 'investor' ? value : null;

  // 'pending' is a placeholder, not a real role — treat it as no-role from either source.
  const storeRole = asPersona(persistedRole);

  if (!hasAuth) return null;
  if (isMockMode && storeRole) return storeRole;
  if (hasUser) return asPersona(profileRole) ?? storeRole;
  return null;
}
